import React, { useState, useEffect } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import WalletService from "../../services/WalletService";
import { validateVoucher as validateVoucherApi } from "../../services/VoucherService";
import { validateTopUpInput, parseTopUpError } from "../../utils/topUpErrorHandler";
import { parseVoucherError } from "../../utils/voucherErrorHandler";

import "./ProfileModals.css";

const TopUpModal = ({ isOpen, onClose, token, walletId, currency, onToppedUp }) => {
  // Top-up amount selection
  const [selectedAmount, setSelectedAmount] = useState(null);

  // Voucher validation state
  const [voucher, setVoucher] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [isVoucherValid, setIsVoucherValid] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [validating, setValidating] = useState(false);
  const [topUpAmounts, setTopUpAmounts] = useState([]);

  // General top-up form state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadAmounts = async () => {
      if (isOpen && currency) {
        const amounts = await WalletService.getTopUpAmounts(currency);
        setTopUpAmounts(amounts);
        setSelectedAmount(amounts[0] || 5);
      }
    };

    loadAmounts();
  }, [isOpen, currency]);

  const resetVoucherState = () => {
    setVoucher("");
    setPromoDiscount(0);
    setIsVoucherValid(null);
    setVoucherError("");
    setValidating(false);
  };

  const resetState = () => {
    setSelectedAmount(5);
    resetVoucherState();
    setError("");
    setSuccess("");
    setSending(false);
  };

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen]);

  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    if (promoDiscount > 0 && amount < promoDiscount) {
      setPromoDiscount(0);
      setIsVoucherValid(null);
    }
    setError("");
  };

  const validateVoucher = async () => {
    if (!voucher.trim()) {
      setIsVoucherValid(null);
      setVoucherError("");
      setPromoDiscount(0);
      return;
    }

    setValidating(true);
    try {
      const result = await validateVoucherApi(voucher);
      console.log("🧪 FULL VOUCHER RESULT:", result);

      if (result.available && result.promocode?.type === "topup_amount") {
        if (result.promocode.currency === currency) {
          setIsVoucherValid(true);
          setVoucherError("");
          setPromoDiscount(result.promocode.amount);
        } else {
          setIsVoucherValid(false);
          setVoucherError(`Promo currency ${result.promocode.currency} ≠ wallet ${currency}`);
        }
      } else {
        setIsVoucherValid(false);
        setVoucherError("Invalid/expired/non-topup promo");
        setPromoDiscount(0);
      }
    } catch (err) {
      setIsVoucherValid(false);
      setVoucherError(parseVoucherError(err));
      setPromoDiscount(0);
    } finally {
      setValidating(false);
    }
  };

  const handleVoucherKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validateVoucher();
    }
  };

  const getApplyButtonText = () => {
    if (validating) return "Checking...";
    if (isVoucherValid === true) return "Applied";
    if (isVoucherValid === false) return "Invalid";
    return "Apply";
  };

  const getApplyButtonClass = () => {
    if (validating) return "checking";
    if (isVoucherValid === true) return "valid";
    if (isVoucherValid === false) return "invalid";
    return "";
  };

  const handleVoucherChange = (e) => {
    const value = e.target.value;
    setVoucher(value);
    setIsVoucherValid(null);
    setVoucherError("");
    setError("");
  };

  const handleCloseWithRefresh = async () => {
    if (success) {
      onToppedUp();
    }
    onClose();
  };

  const finalAmount = promoDiscount > 0 && isVoucherValid === true ? selectedAmount - promoDiscount : selectedAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateTopUpInput({
      selectedAmount,
      voucher,
      promoDiscount,
      currency,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSending(true);

      const payload = {
        request: {
          wallet_id: walletId,
          amount: finalAmount,
          currency,
          provider: "stripe",
          promocode: isVoucherValid === true ? voucher.trim() : null,
        },
        external: {
          frontend_url: `${window.location.origin}/account`,
          product_name: "Wallet Top-Up",
        },
      };

      console.log("Top-up Payload: ", payload);

      const response = await WalletService.topUp(payload);

      console.log("Top-up Response: ", response);

      if (response.success && response.redirect_url) {
        window.location.href = response.redirect_url;
        return;
      }

      if (response.success) {
        resetVoucherState();
        setSuccess(
          `Top-up successful! +${response.topuped_amount?.toLocaleString() || finalAmount} ${response.currency || currency}`,
        );
        return;
      }
    } catch (err) {
      const friendlyError = parseTopUpError(err);
      setError(friendlyError);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseWithRefresh}
      title="Top-Up Wallet"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={sending}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={sending || !selectedAmount}>
            {sending ? "Processing..." : "Top-Up"}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        {/* Amount selection buttons */}
        <div className="profile-topup-amounts">
          {topUpAmounts.map((amount) => (
            <button
              type="button"
              key={amount}
              className={`profile-topup-amount-button ${selectedAmount === amount ? "selected" : ""}`}
              onClick={() => handleAmountClick(amount)}
              disabled={sending}
            >
              {amount} {currency}
            </button>
          ))}
        </div>

        {/* Voucher input */}
        <label className="profile-voucher-label">
          Voucher (optional)
          <div className="profile-voucher-row">
            <MainInput
              type="text"
              value={voucher}
              onChange={handleVoucherChange}
              onKeyDown={handleVoucherKeyDown}
              disabled={sending}
            />
            <MainButton
              type="button"
              onClick={validateVoucher}
              disabled={!voucher.trim() || validating || sending}
              className={`profile-voucher-button ${getApplyButtonClass()}`}
              size="sm"
            >
              {getApplyButtonText()}
            </MainButton>
          </div>
          {voucherError && (
            <div className="profile-voucher-error">
              <ErrorMessage message={voucherError} onClose={() => setVoucherError("")} size="sm" />
            </div>
          )}
        </label>
        {isVoucherValid === true && promoDiscount > 0 && (
          <div className="profile-promo-discount">
            You'll pay: {(selectedAmount - promoDiscount).toFixed(2)} {currency} (promo: -{promoDiscount.toFixed(2)}{" "}
            {currency})
          </div>
        )}

        {/* Top-up form error/success */}
        {error && (
          <div className="profile-topup-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-topup-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default TopUpModal;
